// Quem decide se o clique direito abre o menu do elemento no quadro branco — e sobre QUAIS
// elementos ele vai agir.
//
// Este arquivo NÃO importa nada, de propósito. É o mesmo critério do `decidir-pan.ts`: sem
// imports, o teste o executa nesta estação sem o `node_modules` do Affine e sem os ~16 min de
// build da Regra 8.7.3. O que sobra no arquivo do upstream é a fiação.
//
// O contexto medido no iPad do operador em 2026-08-27 (card 1851188322332837419): o toque de
// dois dedos no trackpad chega como `contextmenu` com `pointerType=mouse` e `button=2`. Não há
// caminho de código específico de iPad — é o mesmo evento do desktop.

/** O que se sabe no instante do clique direito. */
export type EstadoDoCliqueDireito = {
  /** Fora do edgeless não há canvas nem elemento — o menu é de outro assunto. */
  modoPagina: boolean;
  /**
   * Editando texto dentro de uma nota/forma. Aqui o menu NATIVO do sistema precisa passar —
   * é como se cola, se procura e se corrige no iPad.
   */
  editandoTexto: boolean;
  somenteLeitura: boolean;
  /** Id do elemento sob o ponteiro, ou `null` se o clique caiu no vazio. */
  alvo: string | null;
  /** Ids já selecionados no momento do clique. */
  selecionados: readonly string[];
};

export type MotivoDeNaoAbrir =
  | 'modo-pagina'
  | 'edicao-de-texto'
  | 'somente-leitura'
  | 'sem-alvo';

export type DecisaoDoMenu =
  | { abrir: false; motivo: MotivoDeNaoAbrir }
  | {
      abrir: true;
      /**
       * Ids a selecionar ANTES de abrir, ou `null` para preservar a seleção atual.
       *
       * Preservar é o que faz o menu agir sobre os N elementos quando se clica com o direito
       * em cima de um deles. Sem isso, clicar com o direito num de cinco selecionados
       * colapsaria a seleção para um — e o menu, que lê a seleção, agiria sobre um só. É a
       * mesma regra que a linha da tabela do banco de dados já aplica.
       */
      selecionarAntes: string[] | null;
    };

/**
 * Decide se o clique direito abre o menu do elemento, e sobre o que ele vai agir.
 *
 * A ordem das recusas importa: `edicao-de-texto` vem antes de `sem-alvo` porque, editando,
 * a resposta é "não é comigo" mesmo que haja um elemento sob o ponteiro.
 */
export function decidirMenuDeContexto(
  estado: EstadoDoCliqueDireito
): DecisaoDoMenu {
  if (estado.modoPagina) return { abrir: false, motivo: 'modo-pagina' };
  if (estado.editandoTexto) return { abrir: false, motivo: 'edicao-de-texto' };
  if (estado.somenteLeitura) return { abrir: false, motivo: 'somente-leitura' };
  if (estado.alvo === null) return { abrir: false, motivo: 'sem-alvo' };

  const jaSelecionado = estado.selecionados.includes(estado.alvo);

  return {
    abrir: true,
    selecionarAntes: jaSelecionado ? null : [estado.alvo],
  };
}

/**
 * O retângulo de âncora do menu: 0×0 na posição do ponteiro.
 *
 * O floating-ui posiciona contra qualquer coisa que devolva um retângulo — é o mesmo mecanismo
 * que a barra flutuante já usa para se ancorar nas bordas do elemento selecionado. Um retângulo
 * sem área faz o menu nascer exatamente onde o dedo tocou.
 *
 * ⚠️ Coordenadas de CLIENTE (as do evento), não de modelo. O menu vive na tela, não no canvas:
 * ele não se move nem se redimensiona quando o quadro é panejado ou o zoom muda.
 */
export function retanguloNoPonto(x: number, y: number) {
  return {
    x,
    y,
    width: 0,
    height: 0,
    top: y,
    right: x,
    bottom: y,
    left: x,
  };
}
